import { Typography, useTheme } from "@mui/material";
import * as d3 from "d3";
import React from "react";
import "./Map.css";
import texasCounties from "./texasCounties.json";
import { CountyFacilities, FACILITY_TYPE_TT, MarkerCoordinates } from "./types";

const CHART_DIM = 800;
const chartWidth = CHART_DIM;
const chartHeight = CHART_DIM;

const MARGIN_HACK_PRACTICALLY_HIDDEN = "-10000px";

const diamond = d3.symbol().type(d3.symbolDiamond);
const triangle = d3.symbol().type(d3.symbolTriangle);
const circle = d3.symbol().type(d3.symbolCircle);
const star = d3.symbol().type(d3.symbolStar);

const MARKER_SANE = "sane";
const MARKER_RCC = "rcc";
const MARKER_CAC = "cac";
const MARKER_TT = "tt";

function Map({
  facilityData,
  cacFacilityCoords,
  saneProgramsCoords,
  numCacFacilities,
  rccFacilityCoords,
  numRccFacilities,
  numSanePrograms,
  ttFacilityCoords,
  numTtFacilities,
  selectedCounty,
  onSelectedCountyChange,
  isMobile,
}: {
  facilityData: CountyFacilities;
  saneProgramsCoords: MarkerCoordinates[];
  cacFacilityCoords: MarkerCoordinates[];
  rccFacilityCoords: MarkerCoordinates[];
  ttFacilityCoords: MarkerCoordinates[];
  numRccFacilities: { [countyName: string]: [number, number] };
  numCacFacilities: { [countyName: string]: [number, number] };
  numSanePrograms: { [countyName: string]: [number, number] };
  numTtFacilities: { [countyName: string]: [number, number] };
  selectedCounty?: string;
  onSelectedCountyChange: (p: string) => void;
  isMobile: boolean;
}) {
  const theme = useTheme();
  const mapRef = React.createRef<HTMLDivElement>();
  const divRef = React.createRef<HTMLDivElement>();
  let shownMarkers = React.useRef({
    sanePrograms: true,
    cacFacility: true,
    rccFacility: true,
    ttFacility: true,
  });

  React.useEffect(() => {
    if (!facilityData) return;

    // FIXME: Render according to the data, which is now obtained from the authoritative source

    const projection = d3
      .geoMercator()
      .scale((12 * chartWidth) / Math.PI)
      .rotate([0, 0])
      .center([-99.5, 31])
      .translate([chartWidth / 2, chartHeight / 2]);
    const pathGenerator = d3.geoPath(projection);
    const outerDivContainer = d3.select(divRef.current);
    const mapDivContainer = d3
      .select(mapRef.current)
      .style("display", "flex")
      .style("justify-content", "center")
      ;
    const svgMapGroup = mapDivContainer
      .select("#map")
      .attr("title", "Map")
      .style("width", chartWidth)
      .style("height", chartHeight)
      .classed("map-loading", Object.keys(facilityData).length === 0)
      ;

    const tooltip = outerDivContainer
      .select("#tooltip")
      .style("padding", theme.spacing(2))
      .style("box-shadow", "5px 5px 5px grey")
      .style("position", "absolute")
      .style("background", "#FFF")
      .style("min-width", "250px")
      .style("border", `1px solid ${theme.palette.divider}`)
      .style("margin-left", MARGIN_HACK_PRACTICALLY_HIDDEN);
    let width = tooltip.node()?.getBoundingClientRect().width!;
    let height = tooltip.node()?.getBoundingClientRect().height!;

    const asteriskInfo = outerDivContainer
      .select("#asteriskInfo")
      .style("margin-top", theme.spacing(2))
      .style("word-wrap", "break-word");

    const tooltipTitle = outerDivContainer
      .select("#tooltipTitle")
      .style("font-weight", "bold")
      .style("padding-bottom", theme.spacing(1))

      .style("text-align", "center");
    outerDivContainer.select("table").style("width", "100%");
    tooltip
      .select("#tooltipBody")
      .style("text-align", "left")
      .selectAll("td");
    const tooltipSane = tooltip.select("#tooltip1");
    const tooltipCac = tooltip.select("#tooltip3");
    const tooltipRcc = tooltip.select("#tooltip4");
    const tooltipTt = tooltip.select("#tooltip5");

    function displayNumFacilities(
      num: [number, number] | undefined,
      d: React.MouseEvent
    ) {
      if (num) {
        if (num[1] > 0) {
          asteriskInfo.style("display", "block");
          // nudge the top of the tooltip to accommodate the asterisk text
          const topValue = d.pageY - (height! + 60);
          tooltip.style("top", `${topValue < 0 ? d.pageY + 40 : topValue}px`);
          return `${num[0]} (${num[1]}*)`;
        }
        return num[0];
      }
      return 0;
    }
    function onMouseOver(this: any, d: any) {
      if (isMobile) return;
      const countyName = d.target.__data__.properties.name;

      this.parentNode.appendChild(this);
      d3.select(`#${countyName.replace(/\s/g, "")}`)
        .on("mouseout", onMouseOut);
      tooltipTitle.text(`${countyName} County`);
      tooltip
        .style("left", `${d.pageX - width! / 2}px`)
        .style("top", (dta: any) => {
          var topValue = d.pageY - (height! + 40);
          return `${topValue < 0 ? d.pageY + 40 : topValue}px`;
        })
        .style("margin-left", 0);
      tooltipSane.text(
        displayNumFacilities(numSanePrograms[countyName] ?? [0, 0], d)
      );
      tooltipCac.text(
        displayNumFacilities(numCacFacilities[countyName] ?? [0, 0], d)
      );
      tooltipRcc.text(
        displayNumFacilities(numRccFacilities[countyName] ?? [0, 0], d)
      );
      tooltipTt.text(
        displayNumFacilities(numTtFacilities[countyName] ?? [0, 0], d)
      );
    }

    function onMouseOut(this: any, d: any) {
      if (isMobile) return;

      const selection = d3.select(this);
      if (selection.attr("class") !== "selected") {
        tooltip.style("margin-left", MARGIN_HACK_PRACTICALLY_HIDDEN);
        asteriskInfo.style("display", "none");
      }
    }

    svgMapGroup
      .selectAll("path")
      .data(texasCounties.features)
      .join("path")
      .attr("d", pathGenerator)
      .attr("id", (dta: any) => dta.properties.name.replace(/\s/g, ""))
      .attr("class", (dta: any) => {
        if (facilityData[dta.properties.name] && numSanePrograms[dta.properties.name]) {
          if (numSanePrograms[dta.properties.name][0] > 0) {
            return "sane-present county";
          }
          if (numSanePrograms[dta.properties.name][1] > 0) {
            return "sane-neighbor county";
          }
        }
        return "sane-none county";
      })
      .on("mouseover", onMouseOver)
      .on("click", (d: any) => {
        if (isMobile) return;
        const countyName = d.target.__data__.properties.name;
        onSelectedCountyChange(countyName);
      });

    const styles = isMobile
      ? {
          fontSize: "24px",
          spacing: 40,
          symbolSize: 250,
          markerSize: 100,
          xBase: 15,
          yBase: 528,
          rectWidth: 30,
          rectHeight: 15,
          rectXBase: -15,
          rectYBase: -15,
          labelXBase: 16,
        }
      : {
          fontSize: "16px",
          spacing: 30,
          symbolSize: 100,
          markerSize: 70,
          symbolYBase: 620,
          xBase: 15,
          yBase: 570,
          rectWidth: 22,
          rectHeight: 12,
          rectXBase: -11,
          rectYBase: -10.5,
          labelXBase: 16,
        };

    // map markers
    mapDivContainer
      .select("#ttMarkers")
      .style("pointer-events", "none")
      .selectAll(".star")
      .data(ttFacilityCoords)
      .join("path")
      .attr("d", star.size(styles.markerSize))
      .style("visibility", "visibile")
      .attr("class", "map-marker star")
      .attr("id", (dta: any) => `ttMarkers${dta}`)
      .attr(
        "transform",
        (dta: [number, number]) => `translate(${projection([dta[0], dta[1]])})`
      );

    mapDivContainer
      .select("#saneMarkers")
      .style("pointer-events", "none")
      .selectAll(".bubble")
      .data(saneProgramsCoords)
      .join("path")
      .attr("d", circle.size(styles.markerSize * 0.5))
      .style("visibility", "visibile")
      .attr("class", "map-marker bubble")
      .attr("id", (dta: any) => `saneMarkers${dta}`)
      .attr(
        "transform",
        (dta: [number, number]) => `translate(${projection([dta[0], dta[1]])})`
      )
      .attr("r", 4);

    mapDivContainer
      .select("#cacMarkers")
      .style("pointer-events", "none")
      .selectAll(".triangle")
      .data(cacFacilityCoords)
      .join("path")
      .attr("id", (dta: any) => `cacMarkers${dta}`)
      .attr("class", "map-marker triangle")
      .attr("d", triangle.size(styles.markerSize))
      .attr(
        "transform",
        (dta: [number, number]) => `translate(${projection([dta[0], dta[1]])})`
      );

    mapDivContainer
      .select("#rccMarkers")
      .style("pointer-events", "none")
      .selectAll(".diamond")
      .data(rccFacilityCoords)
      .join("path")
      .attr("id", (dta: any) => `rccMarkers${dta}`)
      .attr("class", "map-marker diamond")
      .attr("d", diamond.size(styles.markerSize))
      .attr(
        "transform",
        (dta: [number, number]) => `translate(${projection([dta[0], dta[1]])})`
      );

    function toggleMarker(marker: string) {
      if (marker === MARKER_SANE) {
        if (shownMarkers.current.sanePrograms) {
          d3.selectAll("#saneMarkers").attr("visibility", "hidden");
          d3.select("#saneLabel").style("text-decoration", "line-through");
          shownMarkers.current.sanePrograms = false;
        } else {
          d3.selectAll("#saneMarkers").attr("visibility", "visibile");
          d3.select("#saneLabel").style("text-decoration", "none");
          shownMarkers.current.sanePrograms = true;
        }
      }
      if (marker === MARKER_CAC) {
        if (shownMarkers.current.cacFacility) {
          d3.selectAll("#cacMarkers").attr("visibility", "hidden");
          d3.select("#cacLabel").style("text-decoration", "line-through");
          shownMarkers.current.cacFacility = false;
        } else {
          d3.selectAll("#cacMarkers").attr("visibility", "visibile");
          d3.select("#cacLabel").style("text-decoration", "none");
          shownMarkers.current.cacFacility = true;
        }
      }
      if (marker === MARKER_RCC) {
        if (shownMarkers.current.rccFacility) {
          d3.selectAll("#rccMarkers").attr("visibility", "hidden");
          d3.select("#rccLabel").style("text-decoration", "line-through");
          shownMarkers.current.rccFacility = false;
        } else {
          d3.selectAll("#rccMarkers").attr("visibility", "visibile");
          d3.select("#rccLabel").style("text-decoration", "none");
          shownMarkers.current.rccFacility = true;
        }
      }
      if (marker === MARKER_TT) {
        if (shownMarkers.current.ttFacility) {
          d3.selectAll("#ttMarkers").attr("visibility", "hidden");
          d3.select("#ttLabel").style("text-decoration", "line-through");
          shownMarkers.current.ttFacility = false;
        } else {
          d3.selectAll("#ttMarkers").attr("visibility", "visibile");
          d3.select("#ttLabel").style("text-decoration", "none");
          shownMarkers.current.ttFacility = true;
        }
      }
    }

    const legend = mapDivContainer
      .select("#legend")
      .attr("title", "Legend")
      .style("pointer-events", "none")
      .style("cursor", "pointer")
      .attr("class", "legend").attr("transform", `translate(${styles.xBase - 15},${styles.yBase + (isMobile ? 50 : 20)})`);

    legend
      .select("#legendTitle")
      .attr("font-weight", "bold")
      .style("font-size", styles.fontSize);

    const symbolYBase = (isMobile ? 6 : 4);

    const saneLegendGroup = legend.select("#saneLegendGroup").attr(
      "transform",
      `translate(${styles.xBase}, ${styles.spacing})`)
      .on("click", () => toggleMarker(MARKER_SANE));

    saneLegendGroup
      .select("#saneSymbol")
      .attr("d", circle.size(styles.symbolSize))
      .style("pointer-events", "auto")
      .attr("class", "map-marker bubble");

    saneLegendGroup
      .select("#saneLabel")
      .text("SANE Program")
      .style("font-size", styles.fontSize)
      .style("pointer-events", "auto")
			.style("dominant-baseline","middle")
      .attr("transform", `translate(${styles.labelXBase}, 1)`);

    const ttLegendGroup = legend.select("#ttLegendGroup").attr(
      "transform",
      `translate(${styles.xBase}, ${styles.spacing * 2})`)
      .on("click", () => toggleMarker(MARKER_TT));

    ttLegendGroup
      .select("#ttSymbol")
      .attr("d", star.size(styles.symbolSize))
      .attr("class", "map-marker star")
      .style("pointer-events", "auto");

    ttLegendGroup
      .select("#ttLabel")
      .text(FACILITY_TYPE_TT)
      .style("font-size", styles.fontSize)
      .style("pointer-events", "auto")
      .attr("dominant-baseline", "middle").attr(
        "transform",
        `translate(${styles.labelXBase}, 1)`
      );

    const cacLegendGroup = legend.select("#cacLegendGroup").attr(
      "transform",
      `translate(${styles.xBase}, ${styles.spacing * 3})`)
      .on("click", () => toggleMarker(MARKER_CAC));

    cacLegendGroup
      .select("#cacSymbol")
      .attr("d", triangle.size(styles.symbolSize))
      .attr("class", "map-marker triangle")
      .style("pointer-events", "auto").attr(
        "transform",
        `translate(0, 1)`);

    cacLegendGroup
      .select("#cacLabel")
      .text("Child Advocacy Center")
      .style("font-size", styles.fontSize)
      .style("pointer-events", "auto")
      .attr("dominant-baseline", "middle")
      .attr(
        "transform",
        `translate(${styles.labelXBase}, 1)`);

    const rccLegendGroup = legend.select("#rccLegendGroup").attr(
      "transform",
      `translate(${styles.xBase}, ${styles.spacing * 4})`)
      .on("click", () => toggleMarker(MARKER_RCC));

    rccLegendGroup
      .select("#rccSymbol")
      .attr("d", diamond.size(styles.symbolSize))
      .attr("class", "map-marker diamond")
      .style("pointer-events", "auto");

    rccLegendGroup
      .select("#rccLabel")
      .text("Rape Crisis Center")
      .style("font-size", styles.fontSize)
      .style("pointer-events", "auto")
      .attr("dominant-baseline", "middle").attr(
        "transform",
        `translate(${styles.labelXBase}, 1)`
      );

    const countyLegendGroup = legend.select("#countyLegendGroup").attr(
      "transform",
      `translate(${styles.xBase}, ${styles.spacing * 5})`);

    countyLegendGroup
      .select("#countySymbol")
      .attr("width", styles.rectWidth)
      .attr("height", styles.rectHeight)
      .attr("class", "county sane-present")
      .attr("transform", `translate(${styles.rectXBase}, ${styles.rectYBase + symbolYBase})`);

    countyLegendGroup
      .select("#countyLabel")
      .text("SAFE/SANE Program in County")
      .style("font-size", styles.fontSize)
      .attr("dominant-baseline", "middle")
      .attr("transform", `translate(${styles.labelXBase}, 1)`);

    const servicedByLegendGroup = legend.select("#servicedByLegendGroup").attr(
      "transform",
      `translate(${styles.xBase}, ${styles.spacing * 6})`);

    servicedByLegendGroup
      .select("#servicedByCountySymbol")
      .attr("width", styles.rectWidth)
      .attr("height", styles.rectHeight)
      .attr("class", "county sane-neighbor")
      .attr("transform", `translate(${styles.rectXBase}, ${styles.rectYBase})`);

    servicedByLegendGroup
      .select("#servicedByCountyLabel")
      .text("Serviced by SAFE/SANE Program in Neighboring County")
      .style("font-size", styles.fontSize)
      .attr("transform", `translate(${styles.labelXBase}, 1)`);

    mapDivContainer
      .select("#legend-info")
      .attr("transform", "translate(520, 585)");

    mapDivContainer
      .select("#loading-message")
      .style("visibility", Object.keys(facilityData).length > 0 ? "hidden" : "unset")
      .attr("transform", "translate(370, 400)")
      .attr("class", "loading-text");
  }, [
    divRef,
    mapRef,
    numSanePrograms,
    onSelectedCountyChange,
    saneProgramsCoords,
    facilityData,
    isMobile,
    cacFacilityCoords,
    numCacFacilities,
    numRccFacilities,
    numTtFacilities,
    rccFacilityCoords,
    ttFacilityCoords,
    theme,
  ]);

  React.useEffect(() => {
    if (!selectedCounty || selectedCounty === "all") return;

    // highlight county
    const node = d3.select(`#${selectedCounty.replace(/\s/g, "")}`);
    node
      .classed("selected", true);
    node.node().parentNode.appendChild(node.node());
    return () => {
      node
        .classed("selected", false)
      var firstChild = node.node().parentNode.firstChild;
      if (firstChild) {
        node.node().parentNode.insertBefore(node.node(), firstChild);
      }
    };
  }, [
    selectedCounty,
    numSanePrograms,
    onSelectedCountyChange,
    saneProgramsCoords,
    shownMarkers,
  ]);

  return (
    <div>
      <div ref={divRef} id="outer-map-container">
        <Typography variant="h3" mt={2} mb={0} textAlign="center">
          Texas Sexual Assault Medical Forensic & Advocacy Services
        </Typography>
        <Typography
          display={"inline-block"}
          mt={2}
          mb={0}
          color="text.secondary"
          textAlign={"center"}
        >
          Select a county to view facilities servicing the area
        </Typography>
        <div id="tooltip">
          <Typography variant="h3" id="tooltipTitle"></Typography>

          <table>
            <tbody id="tooltipBody">
              <tr>
                <td>SANE program</td>
                <td id="tooltip1"></td>
              </tr>
              <tr>
                <td>Tex-TRAC Telehealth</td>
                <td id="tooltip5"></td>
              </tr>
              <tr>
                <td>Child Advocacy Center</td>
                <td id="tooltip3"></td>
              </tr>
              <tr>
                <td>Rape Crisis Center</td>
                <td id="tooltip4"></td>
              </tr>
            </tbody>
          </table>

          <Typography variant="caption" id="asteriskInfo">
            * Facilities in neighboring county
          </Typography>
        </div>

        <div ref={mapRef} id="inner-map-container">
          <svg
            viewBox={`0 0 ${CHART_DIM} ${CHART_DIM + (isMobile ? 30 : 0)}`}
            style={{ maxWidth: CHART_DIM, maxHeight: CHART_DIM, flex: 1}}
          >
            <g id="map" className="map-loading" style={{}}></g>
            <text id="loading-message">
              Loading...
            </text>
            <g id="legend">
              <text id="legendTitle"> Legend (click to toggle)</text>

              <g id="saneLegendGroup">
                <path id="saneSymbol"></path>
                <text id="saneLabel"></text>
              </g>

              <g id="ttLegendGroup">
                <path id="ttSymbol"></path>
                <text id="ttLabel"></text>
              </g>

              <g id="cacLegendGroup">
                <path id="cacSymbol"></path>
                <text id="cacLabel"></text>
              </g>

              <g id="rccLegendGroup">
                <path id="rccSymbol"></path>
                <text id="rccLabel"></text>
              </g>

              <g id="countyLegendGroup">
                <rect id="countySymbol"></rect>
                <text id="countyLabel"></text>
              </g>

              <g id="servicedByLegendGroup">
                <rect id="servicedByCountySymbol"></rect>
                <text id="servicedByCountyLabel"></text>
              </g>
            </g>
            <g id="rccMarkers"></g>
            <g id="cacMarkers"></g>
            <g id="ttMarkers"></g>
            <g id="saneMarkers"></g>
          </svg>
        </div>
      </div>
      <div style={{
        marginTop: theme.spacing(4),
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
      }}>
        <ul
          style={{
            textAlign: "left",
            listStyleType: "none",
            paddingTop: theme.spacing(2),
            padding: 0 /* Remove padding */,
            margin: 0 /* Remove margins */,
          }}
        >
          <li>
            <Typography variant="caption">
              <b>Forensic Nurse</b> and <b>SANE</b>: Registered nurse who has
              attended a 40-hr didactic SANE course or completed an academic
              forensic nursing program.
            </Typography>
          </li>
          <li>
            <Typography variant="caption">
              <b>SANE Program</b>: Facilities with SANEs, including children's
              advocacy centers (CACs) and hospitals or stand-alone facilities
              with SANEs.
            </Typography>
          </li>
          <li>
            <Typography variant="caption">
              <b>SAFE-Ready Facility</b>: "Health care facility designated as a
              sexual assault forensic exam-ready facility" under Section
              323.0015 Texas Health and Safety Code
            </Typography>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default Map;
